/**
 * Care Gaps (Screen D) — the REAL org-wide open-gap worklist, sourced from gold.fact_measure_score
 * (UI Wave-2 A1). Replaces the prior DEFERRED "awaiting sync" holding state: gold holds the live care-gap
 * truth (201 open rate-rows; 152 distinct member x measure open gaps after the pediatric + sentinel
 * filters and rate-id de-dup), so this surface is honestly REAL/gold.
 *
 * Two surfaces, each badged from its OWN server meta.provenance via ProvenanceBadge (never hand-tagged):
 *   1. Summary — KPI cards (open gaps / members with a gap / top domain) + a closure-by-measure table.
 *   2. Worklist — SkyDataGrid with SERVER-SIDE filter (domain, system gap reason), sort, paginate, and a
 *      member drill-through Link to /member/:enterprise_member_key (Member-360).
 *
 * Honesty is preserved end-to-end:
 *   - PACE suppression: measures are shown by NAME + DOMAIN only — never a raw HEDIS/STARS code or
 *     program. These are framed as internal indicators, NOT CMS PACE/HPMS measures, NOT MA Star Ratings.
 *   - Non-cohort caveat: the sandbox is generic eCW FHIR data (0/2299 PACE-confirmed), so these are
 *     structurally-real gaps on a NON-PACE denominator — a persistent warning banner says so.
 *   - H-3: pediatric measures (e.g. Topical Fluoride for Children, Lead Screening in Children) are
 *     EXCLUDED as not-applicable-to-this-population; the excluded count is surfaced honestly.
 *   - M-2: the per-row gap reason is labeled "system gap reason (provisional)" and drives no action.
 *   - Empty reads as honest REAL-empty ("No open care gaps on record"), NEVER a fabricated zero.
 * Rows are never console.logged; each section isolates its own loading/error.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  SkyCard,
  SkyDataGrid,
  SkyDropdown,
  SkyText,
  SkyLoader,
  SkyMessageBar,
  type SkyDataGridColumn,
  type SortDirection,
} from "@skypointcloud/skypoint-ui";
import type {
  CareGapWorklistRow,
  CareGapWorklistStat,
  CareGapWorklistSummary,
  Provenance,
} from "@shared/contracts";
import { fetchEnvelope, ApiProblem } from "../lib/api";
import { usePage } from "../lib/usePage";
import { careGapWorklistSchema, careGapWorklistSummarySchema } from "../lib/schemas/caregaps";
import { COPY, MICROCOPY, humanizeToken } from "../lib/copy";
import { deriveBadge } from "../provenance/deriveBadge";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { PageError } from "../components/PageError";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

const PAGE_SIZE = 25;

const NON_COHORT_CAVEAT =
  "These are internal indicators, not CMS PACE/HPMS measures and not MA Star Ratings. The sandbox is " +
  "generic eCW FHIR data (0 of 2299 members PACE-confirmed), so every gap below is structurally real but " +
  "sits on a NON-PACE denominator: treat it as a signal, not an authoritative PACE worklist.";

// System gap reasons are a small, model-defined enum (M-2: provisional, never an actionable closure
// reason). A friendly label for each; unknown values fall through to the raw string.
const GAP_REASON_LABEL: Record<string, string> = {
  none: MICROCOPY.fieldAbsent,
  pharmacy_dispensing_unavailable: "Pharmacy dispensing unavailable",
  external_facility_event_unobserved: "External facility event unobserved",
  unstructured_documentation: "Unstructured documentation",
};
const GAP_REASON_OPTIONS = Object.entries(GAP_REASON_LABEL).map(([value, text]) => ({ value, text }));

function gapReasonText(v: string | null): string {
  if (!v) return MICROCOPY.fieldAbsent;
  return GAP_REASON_LABEL[v] ?? v;
}

/** Server-sortable worklist columns (must match the route's sort enum). */
const SORTABLE: Record<string, string> = { last_name: "member", measure_name: "measure", domain: "domain" };

const worklistColumns: SkyDataGridColumn<CareGapWorklistRow>[] = [
  {
    key: "last_name",
    header: "Member",
    width: "22%",
    sortable: true,
    dataMode: "server",
    // Drill into Member-360 by the non-PHI surrogate key. SPA Link, no reload.
    renderCell: (_v, row) => (
      <Link to={`/member/${row.enterprise_member_key}`}>
        {row.last_name ?? "Member"}
        {row.first_name ? `, ${row.first_name}` : ""}
      </Link>
    ),
  },
  {
    key: "measure_name",
    header: "Measure",
    width: "34%",
    sortable: true,
    dataMode: "server",
    // PUBLIC label only (name + domain) — never the raw HEDIS/STARS code or program (PACE suppression).
    renderCell: (_v, row) => (
      <div className="flex flex-col">
        <SkyText size={300} weight="medium">
          {row.measure_name ?? "Measure label pending"}
        </SkyText>
        {row.domain && <SkyText size={200}>{humanizeToken(row.domain)}</SkyText>}
      </div>
    ),
  },
  {
    key: "domain",
    header: "Domain",
    width: "18%",
    sortable: true,
    dataMode: "server",
    // P2-2: humanize the snake_case domain token for clinicians ("behavioral_health" -> "Behavioral Health").
    renderCell: (_v, row) => humanizeToken(row.domain),
  },
  {
    key: "system_gap_reason",
    header: "System gap reason (provisional)",
    width: "26%",
    // M-2: provisional, model-derived — surfaced honestly, drives no closure action.
    renderCell: (_v, row) => gapReasonText(row.system_gap_reason),
  },
];

const measureColumns: SkyDataGridColumn<CareGapWorklistStat>[] = [
  {
    key: "measure_name",
    header: "Measure",
    width: "32%",
    renderCell: (_v, row) => (
      <div className="flex flex-col">
        <SkyText size={300} weight="medium">
          {row.measure_name ?? "Measure label pending"}
        </SkyText>
        {row.domain && <SkyText size={200}>{humanizeToken(row.domain)}</SkyText>}
      </div>
    ),
  },
  { key: "domain", header: "Domain", width: "20%", renderCell: (_v, row) => humanizeToken(row.domain) },
  { key: "open_gaps", header: "Open gaps", width: "16%", align: "right" },
  { key: "denominator", header: "Denominator", width: "16%", align: "right" },
  {
    key: "closure_rate",
    header: "Closure rate",
    width: "16%",
    align: "right",
    // Render-shape guard: a null/NaN closure_rate on a drifted row degrades to 0% rather than "NaN%".
    renderCell: (_v, row) => `${Math.round((Number(row.closure_rate) || 0) * 100)}%`,
  },
];

/** A single big KPI value with a caption, in a SkyCard. */
function KpiCard({ value, label, caption }: { value: string; label: string; caption?: string }) {
  return (
    <SkyCard header={{ title: label }}>
      <div className="flex flex-col">
        <SkyText size={700} weight="bold" font="numeric">
          {value}
        </SkyText>
        {caption && <SkyText size={200}>{caption}</SkyText>}
      </div>
    </SkyCard>
  );
}

/** Summary section — KPIs + closure-by-measure table. Badged from its own meta.provenance. */
function SummarySection({
  summary,
  meta,
  loading,
  error,
  onRetry,
}: {
  summary: CareGapWorklistSummary | null;
  meta: Provenance | undefined;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  // H1 provenance gate (mirror Dashboard DecisionTile.showMetric): the KPI headline numbers
  // (totalOpen / membersWithGaps / topDomain) render ONLY when the derived tier is REAL/PARTIAL. A
  // DEFERRED/MOCK summary (e.g. gold not yet synced -> deferredProvenance on 42P01) shows the shared
  // "Awaiting feed (feed-gated, not a zero)" holding state instead of a dangerous 0/0/None, with a
  // ProvenanceBadge adjacent so no headline number ever renders without its provenance.
  const tier = deriveBadge(meta).tier;
  const showMetric = tier === "REAL" || tier === "PARTIAL";
  return (
    <div className="flex flex-col gap-4">
      {loading && <SkyLoader size="small" label="Loading care-gap summary" />}
      {error && <PageError title="Could not load the care-gap summary" onRetry={onRetry} />}
      {!loading && !error && !showMetric && (
        <SkyCard
          header={{ title: "Care-gap summary", action: <ProvenanceBadge meta={meta} /> }}
        >
          <SkyText size={300}>{COPY.awaitingFeedHolding}</SkyText>
        </SkyCard>
      )}
      {!loading && !error && showMetric && summary && (
        <>
          <div className="flex items-center justify-between">
            <SkyText size={300} weight="semibold">
              Care-gap summary
            </SkyText>
            <ProvenanceBadge meta={meta} />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <KpiCard
              value={String(summary.totalOpen)}
              label="Open care gaps"
              caption="in denominator, not in numerator (non-PACE denominator)"
            />
            <KpiCard
              value={String(summary.membersWithGaps)}
              label="Members with a gap"
              caption="distinct members carrying at least one open gap"
            />
            <KpiCard
              value={summary.topDomain ? humanizeToken(summary.topDomain) : MICROCOPY.collectionEmpty}
              label="Top domain"
              caption="measure domain with the most open gaps"
            />
          </div>
          {summary.excludedPediatric > 0 && (
            <SkyMessageBar
              intent="info"
              title="Pediatric measures excluded"
              message={`${summary.excludedPediatric} open gap(s) on pediatric measures (e.g. Topical Fluoride for Children, Lead Screening in Children) are excluded as not applicable to this elderly population.`}
            />
          )}
          <SkyCard
            header={{
              title: "Closure by measure",
              description: "Open gaps, denominator, and closure rate per measure (pediatric excluded)",
              action: <ProvenanceBadge meta={meta} />,
            }}
          >
            <SkyDataGrid<CareGapWorklistStat>
              // Render-shape guard: a drifted payload where byMeasure is not an array degrades to an
              // empty grid instead of throwing inside the grid's row map.
              data={Array.isArray(summary.byMeasure) ? summary.byMeasure : []}
              columns={measureColumns}
              getRowId={(_r, i) => String(i)}
              emptyMessage="No open care gaps on record"
            />
          </SkyCard>
        </>
      )}
    </div>
  );
}

/** Worklist section — server-side filter (domain, gap reason) + sort + paginate + member drill. */
function WorklistSection({ domains }: { domains: string[] }) {
  const [rows, setRows] = useState<CareGapWorklistRow[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [provenance, setProvenance] = useState<Provenance | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [domain, setDomain] = useState("");
  const [gapReason, setGapReason] = useState("");
  const [sort, setSort] = useState<string | null>(null);
  const [dir, setDir] = useState<SortDirection | null>(null);
  // attempt is a fetch dep so retry() can re-fire a transient failure without needing a filter change.
  const [attempt, setAttempt] = useState(0);

  // Any filter/sort change returns to page 1 so the user never lands on an out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [domain, gapReason, sort, dir]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (domain) params.set("domain", domain);
    if (gapReason) params.set("gap_reason", gapReason);
    if (sort) params.set("sort", sort);
    if (sort && dir) params.set("dir", dir);
    return params.toString();
  }, [page, domain, gapReason, sort, dir]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchEnvelope<CareGapWorklistRow[]>(`/api/v1/care-gaps/worklist?${queryString}`, undefined, careGapWorklistSchema)
      .then(({ data, meta }) => {
        if (cancelled) return;
        // Render-shape guard (parity with the summary.byMeasure guard): a drifted non-array worklist
        // payload degrades to an empty grid instead of throwing inside the grid's row map.
        setRows(Array.isArray(data) ? data : []);
        if (meta?.total != null) setTotal(meta.total);
        setProvenance(meta?.provenance);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof ApiProblem ? e.problem.title : "Failed to load the worklist.");
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [queryString, attempt]);

  const retry = () => setAttempt((a) => a + 1);

  // Wire the grid sort to the server param via the whitelist (column key -> route sort enum).
  function onSortChange(columnKey: string | null, direction: SortDirection | null) {
    const mapped = columnKey ? SORTABLE[columnKey] : null;
    if (!mapped || !direction) {
      setSort(null);
      setDir(null);
      return;
    }
    setSort(mapped);
    setDir(direction);
  }

  return (
    <SkyCard
      header={{
        title: "Care-gap worklist",
        description: "Open gaps across the panel, by measure name and domain only. Row links to Member-360.",
        action: <ProvenanceBadge meta={provenance} />,
      }}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56 max-w-full">
            <SkyDropdown
              label="Domain"
              options={domains.map((d) => ({ value: d, text: humanizeToken(d) }))}
              placeholder="All domains"
              clearable
              selectedOptions={domain ? [domain] : []}
              onOptionSelect={(v) => setDomain(v[0] ?? "")}
            />
          </div>
          <div className="w-72 max-w-full">
            <SkyDropdown
              label="System gap reason (provisional)"
              options={GAP_REASON_OPTIONS}
              placeholder="All reasons"
              clearable
              selectedOptions={gapReason ? [gapReason] : []}
              onOptionSelect={(v) => setGapReason(v[0] ?? "")}
            />
          </div>
        </div>
        {error && <PageError title="Could not load the worklist" onRetry={retry} />}
        <SkyDataGrid<CareGapWorklistRow>
          data={rows}
          columns={worklistColumns}
          getRowId={(r) => r.score_key}
          onSortChange={onSortChange}
          pagination
          page={page}
          pageSize={PAGE_SIZE}
          totalItems={total}
          onPageChange={setPage}
          loading={loading}
          emptyMessage="No open care gaps on record for the current filters"
        />
      </div>
    </SkyCard>
  );
}

export function CareGaps() {
  const summaryState = usePage<CareGapWorklistSummary>(
    "/api/v1/care-gaps/worklist/stats",
    careGapWorklistSummarySchema,
  );
  const domains = useMemo(() => {
    // Render-shape guard: only map byMeasure when it is genuinely an array (a drifted payload never throws).
    const rows = summaryState.data?.byMeasure;
    const list = Array.isArray(rows) ? rows : [];
    return [...new Set(list.map((r) => r.domain).filter((d): d is string => !!d))].sort();
  }, [summaryState.data]);

  return (
    <>
      <PageHeader
        crumbs={[{ key: "home", label: "skyCare" }, { key: "care-gaps", label: "Care Gaps" }]}
        title="Care Gaps"
        description="The org-wide open-gap worklist, read live from gold measure scores and shown by measure name and domain only."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        {/* P1-2: the structural non-cohort caveat is stated ONCE here, calmer (info) than an elevated
            safety alert, so the genuinely safety-critical banners keep their salience. */}
        <SkyMessageBar intent="info" title="Internal indicators on a non-PACE denominator" message={NON_COHORT_CAVEAT} />
        <SummarySection
          summary={summaryState.data}
          meta={summaryState.meta?.provenance}
          loading={summaryState.loading}
          error={summaryState.error}
          onRetry={summaryState.retry}
        />
        <WorklistSection domains={domains} />
      </section>
      <PageFooter />
    </>
  );
}
